import { useState, useEffect, type FormEvent } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { Link, useNavigate } from '@tanstack/react-router';
import { authService } from '@/auth/hooks';
import { ApiError } from '@/auth/api';
import { tokenStore } from '@/auth/token-store';
import { Brand } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { OtpInput } from '@/components/ui/otp-input';
import { PreAuthFeedback } from '@/components/pre-auth-feedback';
import { useAdminHostTenant } from '@/data/admin-host';
import { isTenantAdminHost } from '@/config';

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // P1 (PLAN Q1): passwordless owners sign in by emailed code; the password
  // form stays the default and is untouched for existing accounts.
  const [mode, setMode] = useState<'password' | 'code'>('password');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Unit D: one lock across all three submit paths — Enter-key re-submits
  // while a request is in flight are dropped (replaces the old loading state).
  const submitLock = useActionLock();
  // FAD A3: on admin.<zone> the sign-in page wears the fleet's identity,
  // with "powered by Breaker" as the only Breaker mark. Central unchanged.
  const adminTenant = useAdminHostTenant();
  const hostTenant = isTenantAdminHost() ? (adminTenant.data ?? null) : null;

  // Show a friendly notice if we landed here because of a session-expired bounce.
  useEffect(() => {
    if (tokenStore.consumeExpiredFlag()) {
      setNotice('Your session expired. Sign in again to continue.');
    }
  }, []);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submitLock.run(async () => {
      try {
        await authService.login(email.trim(), password);
        await navigate({ to: '/' });
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.status === 401 ? 'Email or password is incorrect.' : err.message);
        } else {
          setError('Something went wrong. Try again.');
        }
      }
    });
  }

  function onSendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submitLock.run(async () => {
      try {
        await authService.otpRequest(email.trim());
        setCodeSent(true);
      } catch {
        // The rail 204s without enumeration; a network failure is the only
        // realistic error here.
        setError('Could not send the code. Try again.');
      }
    });
  }

  function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submitLock.run(async () => {
      try {
        const result = await authService.otpVerify(email.trim(), code.trim());
        if (result === 'no_account') {
          setError('No account with that email — start a fleet from the signup page.');
          return;
        }
        await navigate({ to: '/' });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "That code didn't work. Try again.");
      }
    });
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Subtle decorative banner — single rule; brand-tinted on tenant hosts. */}
      <div
        className="h-1 bg-breaker-identity w-full"
        style={
          hostTenant?.brand.primaryHex
            ? { backgroundColor: hostTenant.brand.primaryHex }
            : undefined
        }
      />

      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="mb-10 text-center">
            {hostTenant ? (
              <>
                {hostTenant.brand.logoUrl ? (
                  <img
                    src={hostTenant.brand.logoUrl}
                    alt={hostTenant.fleetName}
                    className="h-12 w-auto max-w-[220px] object-contain mx-auto"
                  />
                ) : (
                  <span className="text-3xl font-semibold tracking-tight text-breaker-ink">
                    {hostTenant.fleetName}
                  </span>
                )}
                <p className="text-sm text-breaker-mute mt-2 inline-flex items-baseline gap-1">
                  Dispatch · powered by <Brand size="sm" />
                </p>
              </>
            ) : (
              <>
                <Brand size="lg" />
                <p className="text-sm text-breaker-mute mt-2 tracking-wide uppercase">
                  Dispatch
                </p>
              </>
            )}
          </div>

          <form
            onSubmit={mode === 'password' ? onSubmit : codeSent ? onVerifyCode : onSendCode}
            className="space-y-4"
          >
            {notice ? (
              <div className="rounded-md bg-breaker-ground-2 border border-breaker-line-2 px-3 py-2 text-sm text-breaker-ink-2">
                {notice}
              </div>
            ) : null}
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@fleet.example"
                required
                autoFocus
              />
            </Field>

            {mode === 'password' ? (
              <Field label="Password" htmlFor="password">
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>
            ) : codeSent ? (
              <div>
                <label className="block text-xs font-medium text-breaker-ink-2 mb-1.5">
                  Code from your email
                </label>
                <OtpInput id="loginCode" value={code} onChange={setCode} autoFocus />
              </div>
            ) : (
              <p className="text-sm text-breaker-mute">
                We&rsquo;ll email you a 6-digit sign-in code.
              </p>
            )}

            {error ? (
              <div className="rounded-md bg-breaker-red/10 border border-breaker-red/30 px-3 py-2 text-sm text-breaker-red-deep">
                {error}
              </div>
            ) : null}

            <Button type="submit" className="w-full" loading={submitLock.locked} size="lg">
              {mode === 'password' ? 'Sign in' : codeSent ? 'Verify' : 'Email me a code'}
            </Button>

            <button
              type="button"
              className="w-full text-center text-xs text-breaker-mute hover:text-breaker-identity transition-colors"
              onClick={() => {
                setMode(mode === 'password' ? 'code' : 'password');
                setCodeSent(false);
                setCode('');
                setError(null);
              }}
            >
              {mode === 'password' ? 'Sign in with an emailed code instead' : 'Use a password instead'}
            </button>
          </form>

          {hostTenant ? (
            <p className="mt-8 text-center text-xs text-breaker-mute">
              Sign-in for {hostTenant.fleetName} staff. Different fleet?{' '}
              <a
                href="https://app.breakernetwork.app"
                className="text-breaker-identity hover:underline"
              >
                app.breakernetwork.app
              </a>
            </p>
          ) : (
            <p className="mt-8 text-center text-xs text-breaker-mute">
              New here?{' '}
              <Link to="/signup" className="text-breaker-identity hover:underline">
                Start your fleet
              </Link>{' '}
              · Have an invite code?{' '}
              <Link to="/join" className="text-breaker-identity hover:underline">
                Join your fleet
              </Link>
            </p>
          )}

          {/* Support v1: pre-auth door — quiet by design, below everything. */}
          <div className="mt-6">
            <PreAuthFeedback prefillEmail={email} />
          </div>
        </div>
      </main>

      <footer className="px-6 py-6 text-center text-xs text-breaker-mute border-t border-breaker-line">
        © Breaker · Built for UK couriers
      </footer>
    </div>
  );
}
